import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SubscribeForm } from "@/components/newsletter/SubscribeForm";
import { LeadMagnetSection } from "@/components/LeadMagnetSection";
import { blogApi, type BlogPost } from "@/lib/api/blog";
import blogPreviewSnapshotRaw from "./data/blog-preview.json";
import externalFeeds from "@/data/external-feeds.json";
import {
    ArrowRight, BookOpen, Download, CheckCircle2, Clock, ExternalLink
} from "lucide-react";

// Snapshot generato in prebuild da scripts/fetch-blog-data.mjs.
// Il JSON è untyped: lo restringiamo a BlogPost[] per dare a react-query
// l'initialData con la firma corretta, senza ricorrere ad 'as never'.
// NB: si trova dopo gli import (non tra) per rispettare import/first.
const blogPreviewSnapshot = blogPreviewSnapshotRaw as BlogPost[];

// ─── Fade-in on scroll ───
function FadeIn({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.12 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return (
        <div ref={ref} className={className} suppressHydrationWarning
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)",
                transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms` }}>
            {children}
        </div>
    );
}

// ─── Color palette (light theme) ───
const C = {
    bg: "#F3F3F5",
    bgAlt: "#EAEAEE",
    bgCard: "#FFFFFF",
    bgDark: "#1A1A2E",
    text: "#1A1A2E",
    textMuted: "#5A5A72",
    textLight: "#68687F",
    accent: "#2563EB",
    accentDark: "#1D4ED8",
    accentLight: "#DBEAFE",
    purple: "#7C3AED",
    green: "#059669",
    amber: "#D97706",
    border: "rgba(26,26,46,0.08)",
    borderMd: "rgba(26,26,46,0.12)",
    shadow: "0 1px 3px rgba(26,26,46,0.06), 0 8px 32px rgba(26,26,46,0.04)",
    shadowHover: "0 8px 40px rgba(37,99,235,0.10), 0 2px 8px rgba(26,26,46,0.06)",
};


// ─── Blog preview for homepage ───
function formatDate(dateStr: string | null): string {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

function BlogPreviewSection() {
    const { data: posts } = useQuery({
        queryKey: ["blog-preview"],
        queryFn: () => blogApi.getPosts({ limit: 3 }),
        initialData: blogPreviewSnapshot,
        // Lo snapshot è freschissimo appena buildato; anche a metà giornata
        // il blog cambia raramente, quindi consideriamo l'initialData valida
        // per 24h e evitiamo un refetch a ogni load della home.
        staleTime: 24 * 60 * 60 * 1000,
    });

    if (!posts || posts.length === 0) return null;

    return (
        <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bg }}>
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                <FadeIn>
                    <div style={{ textAlign: "center", marginBottom: 48 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 12, display: "block" }}>
                            Dal blog
                        </span>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10, color: C.text }}>
                            Approfondimenti su AI e persone
                        </h2>
                        <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 520, margin: "0 auto" }}>
                            Riflessioni e guide pratiche per capire l'intelligenza artificiale con metodo
                        </p>
                    </div>
                </FadeIn>

                <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
                    {posts.map((post, i) => (
                        <FadeIn key={post.slug} delay={i * 100}>
                            <Link to={`/blog/${post.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <article className="card-hover" style={{
                                    background: C.bgCard, borderRadius: 16, border: `1px solid ${C.border}`,
                                    padding: 28, display: "flex", flexDirection: "column", gap: 14, height: "100%",
                                    boxShadow: C.shadow,
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            {post.category}
                                        </span>
                                        <span style={{ fontSize: 11, color: C.textLight }}>·</span>
                                        <span style={{ fontSize: 11, color: C.textLight, display: "flex", alignItems: "center", gap: 3 }}>
                                            <Clock size={11} />{` ${post.reading_time} min`}
                                        </span>
                                    </div>
                                    <h3 style={{
                                        fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700,
                                        lineHeight: 1.35, color: C.text, margin: 0,
                                    }}>
                                        {post.title}
                                    </h3>
                                    <p style={{ fontSize: 14, lineHeight: 1.65, color: C.textMuted, margin: 0, flex: 1 }}>
                                        {post.excerpt.length > 120 ? post.excerpt.slice(0, 120) + "…" : post.excerpt}
                                    </p>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: C.accent, marginTop: 4 }}>
                                        Leggi <ArrowRight size={13} />
                                    </div>
                                </article>
                            </Link>
                        </FadeIn>
                    ))}
                </div>

                <FadeIn delay={300}>
                    <div style={{ textAlign: "center", marginTop: 36 }}>
                        <Link to="/blog" style={{
                            display: "inline-flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600,
                            color: C.accent, textDecoration: "none",
                        }}>
                            Tutti gli articoli <ArrowRight size={15} />
                        </Link>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// ─── External feeds section: HW Upgrade + Substack ───
function ExternalFeedsSection() {
    const hwLatest = externalFeeds.hwupgrade?.[0];
    const subLatest = externalFeeds.substack?.[0];
    if (!hwLatest && !subLatest) return null;

    const cardStyle = {
        background: C.bgCard, borderRadius: 16, border: `1px solid ${C.border}`,
        padding: 32, display: "flex", flexDirection: "column" as const, gap: 14, height: "100%",
        boxShadow: C.shadow, textDecoration: "none", color: "inherit",
    };
    const badgeStyle = (color: string) => ({
        fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
        textTransform: "uppercase" as const, color, marginBottom: 4,
    });

    return (
        <section style={{ padding: "clamp(60px, 8vw, 100px) 24px", background: C.bgAlt }}>
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                <FadeIn>
                    <div style={{ textAlign: "center", marginBottom: 40 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.purple, marginBottom: 12, display: "block" }}>
                            Dove scrivo
                        </span>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10, color: C.text }}>
                            Articoli e riflessioni sull'Intelligenza Artificiale
                        </h2>
                        <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 640, margin: "0 auto" }}>
                            Scrivo ogni settimana su Hardware Upgrade per la rubrica "IA Upgrade", potete trovare le mie riflessioni personali su Substack e miei articoli su testate nazionali come "Il Corriere della Sera" e "La Stampa"
                        </p>
                    </div>
                </FadeIn>

                <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 24 }}>
                    {hwLatest && (
                        <FadeIn>
                            <a href={hwLatest.url} target="_blank" rel="noopener noreferrer" className="card-hover" style={cardStyle}>
                                <span style={badgeStyle(C.accent)}>IA Upgrade · HWUpgrade.it</span>
                                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: C.text, margin: 0 }}>
                                    {hwLatest.title}
                                </h3>
                                <div style={{ fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                                    <Clock size={12} />{` ${formatDate(hwLatest.date)}`}
                                </div>
                                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: C.accent }}>
                                    Leggi su HWUpgrade <ExternalLink size={13} />
                                </div>
                            </a>
                        </FadeIn>
                    )}
                    {subLatest && (
                        <FadeIn delay={100}>
                            <a href={subLatest.url} target="_blank" rel="noopener noreferrer" className="card-hover" style={cardStyle}>
                                <span style={badgeStyle("#FF6600")}>Substack · Umani per ora</span>
                                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: C.text, margin: 0 }}>
                                    {subLatest.title}
                                </h3>
                                {subLatest.excerpt && (
                                    <p style={{ fontSize: 14, lineHeight: 1.6, color: C.textMuted, margin: 0 }}>
                                        {subLatest.excerpt.length > 120 ? subLatest.excerpt.slice(0, 120) + "…" : subLatest.excerpt}
                                    </p>
                                )}
                                <div style={{ fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                                    <Clock size={12} />{` ${formatDate(subLatest.date)}`}
                                </div>
                                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#FF6600" }}>
                                    Leggi su Substack <ExternalLink size={13} />
                                </div>
                            </a>
                        </FadeIn>
                    )}

                    <FadeIn delay={150}>
                        <a href="https://lucianoballerano.substack.com/p/cosa-faro-da-grande-a-sessantanni" target="_blank" rel="noopener noreferrer" className="card-hover" style={cardStyle}>
                            <span style={badgeStyle("#1A1A2E")}>Il Corriere della Sera</span>
                            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: C.text, margin: 0 }}>
                                Cosa farò da grande, a sessant'anni
                            </h3>
                            <div style={{ fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                                <Clock size={12} /> 24 maggio 2026
                            </div>
                            <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#1A1A2E" }}>
                                Leggi l'articolo su Il Corriere della Sera <ExternalLink size={13} />
                            </div>
                        </a>
                    </FadeIn>

                    <FadeIn delay={200}>
                        <a href="https://www.lastampa.it/tech/2026/06/19/news/ghost_gdp_il_pil_fantasma_dell_intelligenza_artificiale_quanto_costa_davvero_l_efficienza-15664295/" target="_blank" rel="noopener noreferrer" className="card-hover" style={cardStyle}>
                            <span style={badgeStyle("#C8102E")}>La Stampa</span>
                            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: C.text, margin: 0 }}>
                                Ghost GDP: il PIL fantasma dell'intelligenza artificiale
                            </h3>
                            <div style={{ fontSize: 13, color: C.textLight, display: "flex", alignItems: "center", gap: 6 }}>
                                <Clock size={12} /> 19 giugno 2026
                            </div>
                            <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#C8102E" }}>
                                Leggi l'articolo su La Stampa <ExternalLink size={13} />
                            </div>
                        </a>
                    </FadeIn>
                </div>

                <FadeIn delay={300}>
                    <div style={{ display: "flex", gap: 20, justifyContent: "center", marginTop: 36, flexWrap: "wrap" }}>
                        <a href="https://www.hwupgrade.it/tag/IA-Upgrade/" target="_blank" rel="noopener noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: C.accent, textDecoration: "none" }}>
                            Tutti i miei articoli su HWUpgrade <ArrowRight size={14} />
                        </a>
                        <a href="https://lucianoballerano.substack.com" target="_blank" rel="noopener noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#FF6600", textDecoration: "none" }}>
                            Scopri i contenuti del mio canale Substack <ArrowRight size={14} />
                        </a>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// ─── Main component ───
export default function BalleranoSite() {
    const [mobileMenu, setMobileMenu] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinks: Array<{ label: string; href: string; external?: boolean }> = [
        { label: "Chi Sono", href: "/chi-sono" },
        { label: "IA Upgrade", href: "https://www.hwupgrade.it/tag/IA-Upgrade/", external: true },
        { label: "Substack", href: "https://lucianoballerano.substack.com", external: true },
        { label: "Vibe Coder", href: "/vibe-coder" },
        { label: "Newsletter", href: "/newsletter" },
        { label: "Blog", href: "/blog" },
        { label: "Extra", href: "/extra" },
        { label: "Servizi", href: "/servizi" },
        { label: "Metodo", href: "/metodologia" },
    ];

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.text, background: C.bg, overflowX: "hidden" }}>

            <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${C.accent}; color: white; }

        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }

        .text-gradient-hero {
          background: linear-gradient(135deg, #2563EB 0%, #7C3AED 50%, #D97706 100%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .card-lift { transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
        .card-lift:hover { transform: translateY(-4px); box-shadow: ${C.shadowHover}; }

        .nav-link { position: relative; color: rgba(255,255,255,0.78); }
        .nav-link::after {
          content: ""; position: absolute; bottom: -2px; left: 0; width: 0; height: 2px;
          background: white; transition: width 0.3s ease;
        }
        .nav-link:hover::after { width: 100%; }
        .nav-link:hover { color: white; }

        .btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 28px; background: ${C.accent}; color: white;
          text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 15px;
          transition: all 0.3s ease; box-shadow: 0 2px 12px rgba(37,99,235,0.25);
          border: none; cursor: pointer;
        }
        .btn-primary:hover { background: ${C.accentDark}; transform: translateY(-1px); box-shadow: 0 4px 20px rgba(37,99,235,0.35); }

        .btn-secondary {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 28px; background: transparent; color: white;
          text-decoration: none; border-radius: 10px; font-weight: 500; font-size: 15px;
          border: 1.5px solid rgba(255,255,255,0.25); transition: all 0.3s ease; cursor: pointer;
        }
        .btn-secondary:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.4); }

        .desktop-nav { display: flex; }
        .mobile-toggle { display: none; }

        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; text-align: center; }
          .hero-cover { margin: 0 auto; order: -1; }
          .hero-ctas { justify-content: center; }
          .hero-trust { justify-content: center; }
        }

        @media (max-width: 768px) {
          .hero-title { font-size: 2.2rem !important; }
          .section-title { font-size: 1.7rem !important; }
          .grid-2 { grid-template-columns: 1fr !important; gap: 28px !important; }
          .grid-3 { grid-template-columns: 1fr !important; gap: 16px !important; }
          .stats-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .book-band { grid-template-columns: 1fr !important; text-align: center; }
          .book-band-cover { margin: 0 auto; }
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: block !important; }
          .mobile-sticky-cta { display: block !important; }
          section { padding-left: 16px !important; padding-right: 16px !important; }
          footer { padding-bottom: 80px !important; }
        }

        @media (max-width: 480px) {
          .hero-title { font-size: 1.75rem !important; }
          .section-title { font-size: 1.45rem !important; }
          .stats-grid { grid-template-columns: 1fr !important; }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .grid-3 { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>


            {/* ═══════════ NAVIGATION (dark, floating) ═══════════ */}
            <nav style={{
                position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
                padding: scrolled ? "10px 0" : "16px 0",
                background: scrolled ? "rgba(26,26,46,0.95)" : "transparent",
                backdropFilter: scrolled ? "blur(20px)" : "none",
                borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none",
                transition: "all 0.4s ease",
            }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Link to="/" style={{ textDecoration: "none", color: "white", display: "flex", alignItems: "center", gap: 10 }}>
                        <img src="/logo-lb-sq.png" alt="LB" width={34} height={34} style={{ width: 34, height: 34, borderRadius: 9 }} />
                        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, letterSpacing: "-0.02em" }}>
                            Luciano Ballerano
                        </span>
                    </Link>
                    <div style={{ display: "flex", gap: 22, alignItems: "center" }} className="desktop-nav">
                        {navLinks.map(link => {
                            const navStyle = { textDecoration: "none", fontSize: 13, fontWeight: 500, letterSpacing: "0.015em", whiteSpace: "nowrap" as const };
                            return link.external ? (
                                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="nav-link" style={navStyle}>
                                    {link.label}
                                </a>
                            ) : (
                                <Link key={link.href} to={link.href} className="nav-link" style={navStyle}>
                                    {link.label}
                                </Link>
                            );
                        })}
                        <Link to="/umani-per-ora" className="btn-primary"
                            style={{ padding: "8px 18px", fontSize: 13, borderRadius: 8 }}>
                            <BookOpen size={14} /> Il libro
                        </Link>
                    </div>
                    <button onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu"
                        style={{ display: "none", background: "none", border: "none", color: "white", cursor: "pointer", padding: 8 }} className="mobile-toggle">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {mobileMenu ? <path d="M6 6l12 12M6 18L18 6" /> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
                        </svg>
                    </button>
                </div>
                {mobileMenu && (
                    <div style={{ padding: "16px 24px", background: "rgba(26,26,46,0.97)", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                        {navLinks.map(link => {
                            const mobileStyle = { display: "block", padding: "12px 0", color: "rgba(255,255,255,0.8)", textDecoration: "none", fontSize: 16, borderBottom: "1px solid rgba(255,255,255,0.06)" };
                            return link.external ? (
                                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenu(false)} style={mobileStyle}>
                                    {link.label}
                                </a>
                            ) : (
                                <Link key={link.href} to={link.href} onClick={() => setMobileMenu(false)} style={mobileStyle}>
                                    {link.label}
                                </Link>
                            );
                        })}
                        <Link to="/umani-per-ora" onClick={() => setMobileMenu(false)}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 16, padding: "14px 0",
                                background: C.accent, color: "white", textDecoration: "none", borderRadius: 10, fontWeight: 600, fontSize: 15 }}>
                            <BookOpen size={16} /> Il libro
                        </Link>
                    </div>
                )}
            </nav>

            <main>

            {/* ═══════════ HERO (dark, book-forward) ═══════════ */}
            <section id="hero" style={{
                minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden",
                background: "linear-gradient(170deg, #0F0F23 0%, #1A1A2E 40%, #16213E 100%)",
                color: "white",
            }}>
                <div style={{ position: "absolute", top: "10%", right: "8%", width: 350, height: 350, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(37,99,235,0.12), transparent)", animation: "float 9s ease-in-out infinite", filter: "blur(60px)" }} />
                <div style={{ position: "absolute", bottom: "15%", left: "5%", width: 250, height: 250, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(124,58,237,0.08), transparent)", animation: "float 11s ease-in-out infinite 3s", filter: "blur(50px)" }} />

                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "130px 24px 80px", width: "100%", position: "relative", zIndex: 2 }}>
                    <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 56, alignItems: "center" }}>
                        {/* Left: text */}
                        <div>
                            <FadeIn>
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", borderRadius: 20,
                                    background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.2)", marginBottom: 28 }}>
                                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.accent, boxShadow: `0 0 10px ${C.accent}` }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>
                                        Autore · Scrivo di AI e di noi
                                    </span>
                                </div>
                            </FadeIn>

                            <FadeIn delay={100}>
                                <h1 className="hero-title" style={{
                                    fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.4rem, 4.8vw, 4rem)",
                                    fontWeight: 900, lineHeight: 1.1, letterSpacing: "-0.03em", marginBottom: 22,
                                }}>
                                    Non solo come funziona l'AI.<br />
                                    <span className="text-gradient-hero">Ma cosa significa per noi.</span>
                                </h1>
                            </FadeIn>

                            <FadeIn delay={200}>
                                <p style={{ fontSize: "clamp(16px, 2.2vw, 19px)", lineHeight: 1.75, color: "rgba(255,255,255,0.7)", maxWidth: 520, marginBottom: 34 }}>
                                    Scrivo di intelligenza artificiale e di persone — un libro, una newsletter settimanale e articoli.
                                    Per capire l'AI davvero, senza hype e senza paura.
                                </p>
                            </FadeIn>

                            <FadeIn delay={300}>
                                <div className="hero-ctas" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 30 }}>
                                    <Link to="/umani-per-ora" className="btn-primary">
                                        <BookOpen size={16} /> Scopri il libro
                                    </Link>
                                    <a href="https://lucianoballerano.substack.com" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                                        Iscriviti su Substack
                                    </a>
                                </div>
                            </FadeIn>

                            <FadeIn delay={400}>
                                <div className="hero-trust" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.45)" }}>
                                        18 anni in NVIDIA · 20 paesi · 2.000+ professionisti formati
                                    </span>
                                </div>
                            </FadeIn>
                        </div>

                        {/* Right: book cover */}
                        <FadeIn delay={200} className="hero-cover">
                            <Link to="/umani-per-ora" style={{ display: "block", maxWidth: 320 }}>
                                <img src="/book-cover-sm.webp"
                                    alt="Copertina: Umani per ora — Luciano Ballerano" fetchPriority="high" decoding="async"
                                    width={520} height={780}
                                    style={{ width: "100%", height: "auto", borderRadius: 14,
                                        boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 4px 16px rgba(37,99,235,0.2)" }} />
                            </Link>
                        </FadeIn>
                    </div>
                </div>
            </section>


            {/* ═══════════ IL LIBRO (band) ═══════════ */}
            <section style={{ padding: "clamp(56px, 8vw, 90px) 24px", background: C.bgCard, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 980, margin: "0 auto" }}>
                    <div className="book-band" style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 40, alignItems: "center" }}>
                        <FadeIn className="book-band-cover">
                            <Link to="/umani-per-ora" style={{ display: "block" }}>
                                <img src="/book-cover-sm.webp" alt="Umani per ora" width={520} height={780} loading="lazy" decoding="async"
                                    style={{ width: 180, height: "auto", borderRadius: 10, boxShadow: C.shadowHover }} />
                            </Link>
                        </FadeIn>
                        <FadeIn delay={100}>
                            <div>
                                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.amber, marginBottom: 10, display: "block" }}>
                                    Il libro
                                </span>
                                <h2 className="section-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)",
                                    fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10, color: C.text }}>
                                    Umani per ora
                                </h2>
                                <p style={{ fontSize: 17, color: C.textMuted, lineHeight: 1.7, marginBottom: 22, maxWidth: 560 }}>
                                    Sopravvivere e reinventarsi nell'era dell'intelligenza artificiale. Un libro su cosa cambia
                                    davvero — nel lavoro e nella vita — e su come restare protagonisti, non spettatori.
                                </p>
                                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                                    <a href="https://www.amazon.it/dp/B0GWNRFSBK" target="_blank" rel="noopener noreferrer"
                                        className="btn-primary" style={{ background: "linear-gradient(135deg, #FF9900, #FF6600)", color: "#1A1A2E", boxShadow: "0 2px 12px rgba(255,153,0,0.3)" }}>
                                        Acquista su Amazon <ArrowRight size={16} />
                                    </a>
                                    <Link to="/umani-per-ora" style={{ display: "inline-flex", alignItems: "center", gap: 8,
                                        fontSize: 15, fontWeight: 600, color: C.accent, textDecoration: "none", padding: "14px 0" }}>
                                        Tutti i dettagli <ArrowRight size={15} />
                                    </Link>
                                </div>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>


            {/* ═══════════ DOVE SCRIVO ═══════════ */}
            <ExternalFeedsSection />

            {/* ═══════════ APPROFONDIMENTI (DAL BLOG) ═══════════ */}
            <BlogPreviewSection />

            {/* ═══════════ NEWSLETTER ═══════════ */}
            <section id="newsletter" style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bg, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "center" }}>
                    <FadeIn>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 14,
                            background: `${C.amber}10`, border: `1px solid ${C.amber}25`, marginBottom: 16 }}>
                            <span style={{ fontSize: 14 }}>📡</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.amber, letterSpacing: "0.06em" }}>NEWSLETTER SETTIMANALE</span>
                        </div>
                        <h2 className="section-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12, color: C.text }}>
                            Segnali AI
                        </h2>
                        <p style={{ fontSize: 15, color: C.textMuted, marginBottom: 28, lineHeight: 1.7 }}>
                            Ogni lunedì le notizie AI che contano. Niente hype, solo copertura chiara e neutrale. Curata con AI, verificata da un umano.
                        </p>
                        <SubscribeForm variant="large" />
                        <div style={{ marginTop: 20 }}>
                            <Link to="/newsletter" style={{ fontSize: 14, color: C.amber, fontWeight: 600, textDecoration: "none" }}>
                                Leggi i numeri precedenti →
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════════ CHI SONO / perché ascoltarmi ═══════════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bgCard, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 1000, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{ textAlign: "center", marginBottom: 40 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 12, display: "block" }}>
                                Chi sono
                            </span>
                            <h2 className="section-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12, color: C.text }}>
                                Racconto l'AI, senza hype e senza paura
                            </h2>
                            <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 640, margin: "0 auto", lineHeight: 1.7 }}>
                                Scrivo, analizzo e divulgo l'intelligenza artificiale per chi vuole capirla davvero: libri, una newsletter, articoli.
                                Analisi critica e indipendente — cosa cambia, perché, e cosa significa per noi. Alle spalle 18 anni in NVIDIA e
                                migliaia di professionisti formati sull'AI in Europa: la competenza tecnica da cui nasce uno sguardo lucido, non promozionale.
                            </p>
                        </div>
                    </FadeIn>

                    <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, textAlign: "center", marginBottom: 32 }}>
                        {[
                            { val: "18", suf: " anni", lab: "in NVIDIA", color: C.accent },
                            { val: "20", suf: " paesi", lab: "copertura EMEA", color: C.purple },
                            { val: "2.000", suf: "+", lab: "professionisti formati", color: C.green },
                            { val: "3", suf: "", lab: "testate dove scrivo", color: C.amber },
                        ].map((s, i) => (
                            <FadeIn key={i} delay={i * 80}>
                                <div style={{ padding: "24px 16px" }}>
                                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 900, color: s.color, lineHeight: 1 }}>
                                        {s.val}<span style={{ fontSize: 28 }}>{s.suf}</span>
                                    </div>
                                    <div style={{ fontSize: 14, color: C.textMuted, marginTop: 8, fontWeight: 500 }}>{s.lab}</div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>

                    <FadeIn delay={200}>
                        <div style={{ textAlign: "center" }}>
                            <Link to="/chi-sono" style={{ fontSize: 14, color: C.accent, fontWeight: 600, textDecoration: "none" }}>
                                La mia storia →
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════════ PROGETTI ═══════════ */}
            <section style={{ padding: "clamp(48px, 8vw, 80px) 24px", background: C.bg, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
                    <FadeIn>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.purple, marginBottom: 12, display: "block" }}>
                            Thought Leadership
                        </span>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 24, color: C.text }}>
                            Progetti & Pubblicazioni
                        </h2>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 20 }}>
                            {[
                                { name: "Umani per ora", emoji: "📖", color: C.amber, url: "/umani-per-ora" },
                                { name: "Wendy Storyteller", emoji: "✨", color: C.accent, url: "https://wendystoryteller.com/" },
                                { name: "BravoCane", emoji: "🐕", color: C.green, url: "https://www.bravocane.shop/" },
                                { name: "IA Upgrade", emoji: "📡", color: C.amber, url: "https://www.hwupgrade.it/tag/IA-Upgrade/" },
                            ].map((p, i) => {
                                const content = <><span style={{ marginRight: 6 }}>{p.emoji}</span>{p.name}</>;
                                const tagStyle = { padding: "8px 16px", borderRadius: 10, background: C.bgCard,
                                    border: `1px solid ${C.border}`, fontSize: 14, color: C.text, fontWeight: 500, textDecoration: "none" } as const;
                                return p.url.startsWith("http") ? (
                                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={tagStyle}>{content}</a>
                                ) : (
                                    <Link key={i} to={p.url} style={tagStyle}>{content}</Link>
                                );
                            })}
                        </div>
                        <Link to="/progetti" style={{ fontSize: 14, color: C.purple, fontWeight: 600, textDecoration: "none" }}>
                            Scopri tutti i progetti →
                        </Link>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════════ RISORSA PER I LETTORI — Checklist PDF ═══════════ */}
            <section style={{
                padding: "clamp(48px, 8vw, 80px) 24px",
                background: `linear-gradient(135deg, ${C.accent}08, ${C.purple}08)`,
                borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
            }}>
                <div style={{ maxWidth: 700, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{
                            padding: "clamp(28px, 4vw, 44px)", borderRadius: 20,
                            background: C.bgCard, border: `1px solid ${C.borderMd}`,
                            boxShadow: "0 4px 32px rgba(37,99,235,0.08), 0 1px 4px rgba(26,26,46,0.04)",
                            textAlign: "center",
                        }}>
                            <div style={{
                                display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 14,
                                background: `${C.green}10`, border: `1px solid ${C.green}25`, marginBottom: 20,
                            }}>
                                <span style={{ fontSize: 14 }}>🎁</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: C.green, letterSpacing: "0.06em" }}>RISORSA PER I LETTORI</span>
                            </div>

                            <h2 style={{
                                fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 3vw, 2rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12, color: C.text,
                            }}>
                                AI in Azienda: Checklist 7 Step
                            </h2>

                            <p style={{ fontSize: 15, lineHeight: 1.7, color: C.textMuted, maxWidth: 520, margin: "0 auto 24px" }}>
                                Una guida pratica per capire da dove iniziare con l'AI. Scaricala gratis, nessuna email richiesta.
                            </p>

                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 28 }}>
                                {["Assessment readiness", "Priorità d'intervento", "Quick wins immediati", "ROI atteso"].map((item) => (
                                    <span key={item} style={{
                                        display: "inline-flex", alignItems: "center", gap: 6,
                                        padding: "6px 14px", borderRadius: 8, background: C.bg,
                                        fontSize: 13, color: C.textMuted, fontWeight: 500,
                                    }}>
                                        <CheckCircle2 size={14} color={C.green} />{` ${item}`}
                                    </span>
                                ))}
                            </div>

                            <a href="/AI-in-Azienda-Checklist-7-Step.pdf" download
                                className="btn-primary" style={{ padding: "14px 32px", fontSize: 15, borderRadius: 12 }}>
                                <Download size={18} /> Scarica la Checklist gratuita
                            </a>

                            <p style={{ fontSize: 12, color: C.textLight, marginTop: 14 }}>PDF · Download diretto · Nessuna registrazione</p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════════ LEAD MAGNET ═══════════ */}
            <LeadMagnetSection />

            {/* ═══════════ CTA FINALE — Seguimi (dark) ═══════════ */}
            <section id="seguimi" style={{
                padding: "clamp(60px, 10vw, 100px) 24px",
                background: "linear-gradient(170deg, #0F0F23 0%, #1A1A2E 50%, #16213E 100%)",
                color: "white", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: "20%", right: "10%", width: 300, height: 300, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(37,99,235,0.1), transparent)", filter: "blur(60px)" }} />

                <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2 }}>
                    <FadeIn>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: 16, display: "block" }}>
                            Seguimi
                        </span>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 700,
                            letterSpacing: "-0.02em", marginBottom: 16 }}>
                            Resta nel flusso
                        </h2>
                        <p style={{ fontSize: 17, lineHeight: 1.7, color: "rgba(255,255,255,0.6)", marginBottom: 36 }}>
                            Il modo migliore per seguirmi è il mio canale Substack: riflessioni e analisi sull'AI e su di noi.
                            Oppure trovami dove scrivo e pubblico.
                        </p>
                    </FadeIn>

                    <FadeIn delay={150}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center" }}>
                            <a href="https://lucianoballerano.substack.com" target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ padding: "16px 36px", fontSize: 16, borderRadius: 12 }}>
                                Iscriviti al mio canale Substack
                            </a>

                            <div style={{ display: "flex", gap: 20, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
                                {[
                                    { label: "LinkedIn", href: "https://www.linkedin.com/in/ballerano" },
                                    { label: "IA Upgrade", href: "https://www.hwupgrade.it/tag/IA-Upgrade/" },
                                    { label: "luciano@ballerano.com", href: "mailto:luciano@ballerano.com" },
                                ].map((link, i) => (
                                    <a key={i} href={link.href} target={link.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                                        style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: 14, fontWeight: 500, transition: "color 0.3s" }}>
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            </main>

            {/* ═══════════ STICKY MOBILE CTA ═══════════ */}
            <div className="mobile-sticky-cta" style={{
                display: "none", position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99,
                padding: "12px 16px", background: "rgba(26,26,46,0.97)",
                backdropFilter: "blur(16px)", borderTop: "1px solid rgba(255,255,255,0.1)",
            }}>
                <Link to="/umani-per-ora"
                    style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                        color: "white", textDecoration: "none", borderRadius: 12, fontWeight: 700,
                        fontSize: 15, boxShadow: "0 4px 24px rgba(59,130,246,0.35)",
                    }}>
                    <BookOpen size={16} /> Scopri il libro
                </Link>
            </div>

            {/* ═══════════ FOOTER ═══════════ */}
            <footer style={{ padding: "36px 24px", background: "#0D0D1A", color: "white", textAlign: "center" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 14 }}>
                        <img src="/logo-lb-sq.png" alt="LB" width={26} height={26} loading="lazy" style={{ width: 26, height: 26, borderRadius: 7 }} />
                        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: "rgba(255,255,255,0.8)" }}>Luciano Ballerano</span>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>· Autore</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
                        {[
                            ["/chi-sono", "Chi sono"],
                            ["/servizi", "Lavora con me"],
                            ["/privacy-policy", "Privacy Policy"],
                            ["/cookie-policy", "Cookie Policy"],
                            ["/termini-servizio", "Termini e Condizioni"],
                        ].map(([href, label]) => (
                            <Link key={href} to={href} style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>{label}</Link>
                        ))}
                    </div>
                    <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                        {`© ${new Date().getFullYear()} Luciano Ballerano. Tutti i diritti riservati.`}
                    </p>
                </div>
            </footer>

        </div>
    );
}
